<template>
  <div class="carteira-dashboard-vue">
    <div class="kanban-filters carteira-dashboard-vue__filters mb-3">
      <div class="kanban-filters__wrapper">
        <div class="kanban-filters__primary">
          <div class="kanban-filter kanban-filter--md">
            <Input
              label="Data inicial"
              v-model="filters.data_ini"
              name="data_ini"
              type="date"
            />
          </div>
          <div class="kanban-filter kanban-filter--md">
            <Input
              label="Data final"
              v-model="filters.data_fim"
              name="data_fim"
              type="date"
            />
          </div>
          <div v-if="podeFiltrarDonos" class="kanban-filter kanban-filter--md">
            <Autocomplete
              label="Dono da carteira"
              name="dono_ids"
              v-model="filters.donos"
              :items="donosOpcoes"
              :multiple="true"
              placeholder="Todos"
              classe="mb-0"
            />
          </div>
          <div class="kanban-filter kanban-filters__actions filter-buttons">
            <button
              v-if="temFiltrosExtras"
              type="button"
              class="btn btn-outline-light btn-sm kanban-filters__toggle collapsed"
              data-toggle="collapse"
              data-target="#carteira-dashboard-extra-filtros"
              aria-expanded="false"
              aria-controls="carteira-dashboard-extra-filtros"
            >
              <i class="fas fa-sliders-h mr-1"></i>
              <span>Mais filtros</span>
              <i class="fas fa-chevron-down ml-1 kanban-filters__toggle-caret"></i>
            </button>
            <button
              type="button"
              class="btn btn-success btn-sm mr-1"
              title="Aplicar filtros"
              :disabled="loading"
              @click="aplicarFiltros"
            >
              <i class="fas fa-search"></i>
            </button>
            <button
              type="button"
              class="btn btn-cancel btn-sm"
              title="Limpar filtros"
              :disabled="loading"
              @click="limparFiltros"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div
          v-if="temFiltrosExtras"
          id="carteira-dashboard-extra-filtros"
          class="kanban-filters__secondary collapse"
        >
          <div class="kanban-filters__secondary-inner">
            <div class="kanban-filter kanban-filter--md">
              <Autocomplete
                label="Coluna da carteira"
                name="fase_ids"
                v-model="filters.colunas"
                :items="colunasOpcoes"
                :multiple="true"
                placeholder="Todas"
                classe="mb-0"
              />
            </div>
            <div class="kanban-filter kanban-filter--md">
              <Autocomplete
                label="Grupo"
                name="b2b_grupo_ids"
                v-model="filters.grupos"
                :items="gruposOpcoes"
                :multiple="true"
                placeholder="Todos"
                classe="mb-0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="erro" class="alert alert-danger">{{ erro }}</div>

    <div v-if="loading" class="carteira-dashboard-vue__loading">
      <i class="fas fa-circle-notch fa-spin"></i>
      Carregando dashboard…
    </div>

    <template v-if="dados">
      <div class="carteira-dashboard-vue__kpi-grid">
        <div
          v-for="card in cardsTotais"
          :key="card.id"
          class="carteira-dashboard-vue__kpi"
          :class="'carteira-dashboard-vue__kpi--' + card.cor"
        >
          <div class="carteira-dashboard-vue__kpi-rail" aria-hidden="true">
            <i :class="card.icon"></i>
          </div>
          <div class="carteira-dashboard-vue__kpi-main">
            <div class="carteira-dashboard-vue__kpi-label">{{ card.label }}</div>
            <div class="carteira-dashboard-vue__kpi-value">{{ card.valor }}</div>
          </div>
        </div>
      </div>

      <div class="carteira-dashboard-vue__charts">
        <div class="carteira-dashboard-vue__panel mb-0">
          <div class="carteira-dashboard-vue__panel-head">
            <h6 class="carteira-dashboard-vue__panel-title">
              <i class="fas fa-handshake"></i>
              Reuniões por dono da carteira
            </h6>
          </div>
          <div class="carteira-dashboard-vue__panel-body">
            <div ref="chartReunioesPorDono" style="min-height: 280px;"></div>
          </div>
        </div>
        <div class="carteira-dashboard-vue__panel mb-0">
          <div class="carteira-dashboard-vue__panel-head">
            <h6 class="carteira-dashboard-vue__panel-title">
              <i class="fas fa-map-marker-alt"></i>
              Visitas por dono da carteira
            </h6>
          </div>
          <div class="carteira-dashboard-vue__panel-body">
            <div ref="chartVisitasPorDono" style="min-height: 280px;"></div>
          </div>
        </div>
        <div class="carteira-dashboard-vue__panel mb-0">
          <div class="carteira-dashboard-vue__panel-head">
            <h6 class="carteira-dashboard-vue__panel-title">
              <i class="fas fa-traffic-light"></i>
              Semáforo da carteira
            </h6>
          </div>
          <div class="carteira-dashboard-vue__panel-body">
            <div ref="chartSemaforo" style="min-height: 280px;"></div>
          </div>
        </div>
      </div>

      <div class="carteira-dashboard-vue__panel" v-if="dados.por_dono && dados.por_dono.length">
        <div class="carteira-dashboard-vue__panel-head">
          <h6 class="carteira-dashboard-vue__panel-title">
            <i class="fas fa-user-tie"></i>
            Por dono da carteira
          </h6>
        </div>
        <div class="carteira-dashboard-vue__panel-body carteira-dashboard-vue__panel-body--muted">
          <div class="carteira-dashboard-vue__dono-grid">
            <article
              v-for="item in dados.por_dono"
              :key="'dono-' + (item.dono_id || 'sem')"
              class="carteira-dashboard-vue__dono-card"
            >
              <div class="carteira-dashboard-vue__dono-card-top">
                <div
                  class="carteira-dashboard-vue__dono-avatar"
                  :class="{ 'carteira-dashboard-vue__dono-avatar--empty': !item.dono_id && !item.imagem_url }"
                >
                  <img
                    v-if="item.imagem_url"
                    :src="item.imagem_url"
                    :alt="item.nome"
                    class="carteira-dashboard-vue__dono-avatar-img"
                  />
                  <template v-else>{{ iniciais(item.nome) }}</template>
                </div>
                <div class="min-w-0">
                  <h6 class="carteira-dashboard-vue__dono-name">{{ item.nome }}</h6>
                  <div class="carteira-dashboard-vue__dono-sub">
                    {{ item.atendidos_periodo }} de {{ item.total_clientes }} atendidos no período
                  </div>
                </div>
              </div>
              <div class="carteira-dashboard-vue__dono-metrics">
                <div class="carteira-dashboard-vue__dono-metric">
                  <span class="carteira-dashboard-vue__dono-metric-value">{{ item.total_clientes }}</span>
                  <span class="carteira-dashboard-vue__dono-metric-label">Clientes</span>
                </div>
                <div class="carteira-dashboard-vue__dono-metric">
                  <span class="carteira-dashboard-vue__dono-metric-value">{{ item.atendidos_periodo }}</span>
                  <span class="carteira-dashboard-vue__dono-metric-label">Atendidos</span>
                </div>
                <div class="carteira-dashboard-vue__dono-metric">
                  <span class="carteira-dashboard-vue__dono-metric-value">{{ item.reunioes_periodo }}</span>
                  <span class="carteira-dashboard-vue__dono-metric-label">Reuniões</span>
                </div>
                <div class="carteira-dashboard-vue__dono-metric">
                  <span class="carteira-dashboard-vue__dono-metric-value">{{ item.visitas_periodo }}</span>
                  <span class="carteira-dashboard-vue__dono-metric-label">Visitas</span>
                </div>
              </div>
              <div class="carteira-dashboard-vue__dono-progress">
                <div class="carteira-dashboard-vue__dono-progress-label">
                  <span>Cobertura no período</span>
                  <span>{{ coberturaPercent(item) }}%</span>
                </div>
                <div class="carteira-dashboard-vue__dono-progress-track">
                  <div
                    class="carteira-dashboard-vue__dono-progress-bar"
                    :style="{ width: coberturaPercent(item) + '%' }"
                  ></div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <div class="carteira-dashboard-vue__panel">
        <div class="carteira-dashboard-vue__panel-head">
          <h6 class="carteira-dashboard-vue__panel-title">
            <i class="fas fa-table"></i>
            Clientes da carteira
          </h6>
        </div>
        <div class="carteira-dashboard-vue__panel-body">
          <DataTable
            name="Clientes da carteira"
            :headers="headersLista"
            :data="listaData"
            :pesquisa="true"
          >
            <template #item.nome="{ item }">
              <a :href="'/painel/clientes/' + item.id">{{ item.nome }}</a>
            </template>
            <template #item.ultima_reuniao_label="{ item }">
              <span
                :class="indicadorClass(item.indicador)"
                :title="indicadorTitle(item.indicador)"
              >
                {{ item.ultima_reuniao_label }}
              </span>
            </template>
            <template #item.dias_sem_contato="{ item }">
              <span
                :class="indicadorClass(item.indicador)"
                :title="indicadorTitle(item.indicador)"
              >
                {{ item.dias_sem_contato === "" || item.dias_sem_contato === null ? "—" : item.dias_sem_contato }}
              </span>
            </template>
          </DataTable>
        </div>
      </div>
    </template>

    <Modal
      modal-id="modalCarteiraDashboardTarefas"
      :title="modalTitulo"
      dialog-class="modal-dialog-centered modal-lg"
    >
      <template #body>
        <div
          v-if="modalLoading"
          key="carteira-dash-tarefas-loading"
          class="text-center py-4 text-muted small"
        >
          <i class="fas fa-circle-notch fa-spin mr-1" aria-hidden="true"></i>
          Carregando tarefas…
        </div>
        <div v-else key="carteira-dash-tarefas-content">
          <div v-if="modalErro" class="alert alert-danger mb-0">{{ modalErro }}</div>
          <div v-else-if="!modalTarefas.length" class="text-muted text-center py-3">
            Nenhuma tarefa encontrada no período.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm table-striped mb-0">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Título</th>
                  <th>Cliente</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in modalTarefas" :key="'tarefa-' + item.id">
                  <td>{{ item.data || "—" }}</td>
                  <td>{{ item.titulo }}</td>
                  <td>
                    <a v-if="item.cliente_id" :href="'/painel/clientes/' + item.cliente_id">
                      {{ item.cliente_nome }}
                    </a>
                    <span v-else>{{ item.cliente_nome }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
      <template #footer>
        <button type="button" class="btn btn-cancel" data-dismiss="modal">Fechar</button>
      </template>
    </Modal>
  </div>
</template>

<script>
import axios from "axios";
import Autocomplete from "../ui/Autocomplete.vue";
import DataTable from "../ui/DataTable.vue";
import Input from "../ui/Input.vue";
import Modal from "../ui/Modal.vue";

function todayYmd() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function firstDayOfMonthYmd() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${m}-01`;
}

const META_URL = "/painel/carteira/dashboard/meta";
const DADOS_URL = "/painel/carteira/dashboard/dados";
const CLIENTES_URL = "/painel/carteira/dashboard/clientes";
const TAREFAS_URL = "/painel/carteira/dashboard/tarefas";

export default {
  name: "CarteiraDashboardApp",
  components: {
    Autocomplete,
    DataTable,
    Input,
    Modal,
  },
  data() {
    return {
      loading: false,
      erro: "",
      podeFiltrarDonos: false,
      colunasCompartilhadas: false,
      donosOpcoes: [],
      gruposOpcoes: [],
      fasesOpcoes: [],
      filters: {
        data_ini: firstDayOfMonthYmd(),
        data_fim: todayYmd(),
        colunas: [],
        donos: [],
        grupos: [],
      },
      dados: null,
      listaData: [],
      chartReunioesPorDonoInstance: null,
      chartVisitasPorDonoInstance: null,
      chartSemaforoInstance: null,
      carregado: false,
      modalLoading: false,
      modalErro: "",
      modalTitulo: "",
      modalTarefas: [],
      modalRequestId: 0,
    };
  },
  computed: {
    colunasOpcoes() {
      return [{ id: "sem_coluna", value: "Sem coluna" }].concat(this.fasesOpcoes || []);
    },
    temFiltrosExtras() {
      return (this.colunasOpcoes && this.colunasOpcoes.length > 0)
        || (this.gruposOpcoes && this.gruposOpcoes.length > 0);
    },
    headersLista() {
      return [
        { text: "Cliente", value: "nome" },
        { text: "Dono da carteira", value: "dono_nome" },
        { text: "Grupo", value: "grupo_nome" },
        { text: "Última reunião/visita", value: "ultima_reuniao_label" },
        { text: "Dias sem contato", value: "dias_sem_contato", align: "right" },
        { text: "Reuniões no período", value: "reunioes_periodo", align: "right", total: true, precision: 0 },
        { text: "Visitas no período", value: "visitas_periodo", align: "right", total: true, precision: 0 },
      ];
    },
    cardsTotais() {
      if (!this.dados || !this.dados.totais) {
        return [];
      }
      const t = this.dados.totais;
      return [
        { id: "total", label: "Clientes na carteira", valor: t.total_clientes, cor: "primary", icon: "fas fa-briefcase" },
        { id: "reunioes", label: "Reuniões no período", valor: t.reunioes_periodo, cor: "success", icon: "fas fa-handshake" },
        { id: "visitas", label: "Visitas no período", valor: t.visitas_periodo, cor: "warning", icon: "fas fa-map-marker-alt" },
        { id: "atendidos", label: "Clientes atendidos no período", valor: t.clientes_atendidos_periodo, cor: "info", icon: "fas fa-user-check" },
        { id: "sem", label: "Sem reunião/visita", valor: t.clientes_sem_contato, cor: "danger", icon: "fas fa-exclamation-circle" },
      ];
    },
  },
  async mounted() {
    this.onTabShown = async () => {
      if (!this.carregado) {
        this.carregado = true;
        await this.carregarMeta();
        await this.aplicarFiltros();
        return;
      }
      await this.$nextTick();
      this.renderCharts();
    };
    document.addEventListener("carteira-dashboard:shown", this.onTabShown);

    const tabPane = this.$el.closest(".tab-pane");
    if (!tabPane || tabPane.classList.contains("active") || tabPane.classList.contains("show")) {
      await this.onTabShown();
    }
  },
  beforeDestroy() {
    if (this.onTabShown) {
      document.removeEventListener("carteira-dashboard:shown", this.onTabShown);
    }
    this.destroyCharts();
  },
  methods: {
    idsFromAutocomplete(items) {
      if (!Array.isArray(items)) {
        return [];
      }
      return items
        .map((item) => (item && typeof item === "object" ? Number(item.id) : Number(item)))
        .filter((id) => id > 0);
    },
    queryParams() {
      const params = {
        data_ini: this.filters.data_ini,
        data_fim: this.filters.data_fim,
      };
      const donoIds = this.idsFromAutocomplete(this.filters.donos);
      const grupoIds = this.idsFromAutocomplete(this.filters.grupos);
      const faseIds = [];
      let semColuna = false;
      (this.filters.colunas || []).forEach((item) => {
        const raw = item && typeof item === "object" ? item.id : item;
        if (raw === "sem_coluna") {
          semColuna = true;
          return;
        }
        const id = Number(raw);
        if (id > 0) {
          faseIds.push(id);
        }
      });
      donoIds.forEach((id, i) => {
        params[`dono_ids[${i}]`] = id;
      });
      grupoIds.forEach((id, i) => {
        params[`b2b_grupo_ids[${i}]`] = id;
      });
      faseIds.forEach((id, i) => {
        params[`fase_ids[${i}]`] = id;
      });
      if (semColuna) {
        params.sem_coluna = 1;
      }
      return params;
    },
    async carregarMeta() {
      try {
        const { data } = await axios.get(META_URL);
        this.podeFiltrarDonos = !!data.pode_filtrar_donos;
        this.colunasCompartilhadas = !!data.colunas_compartilhadas;
        this.donosOpcoes = data.donos || [];
        this.gruposOpcoes = data.grupos || [];
        this.fasesOpcoes = data.fases || [];
      } catch (e) {
        this.erro = "Não foi possível carregar os filtros.";
      }
    },
    async aplicarFiltros() {
      this.erro = "";
      this.loading = true;
      try {
        const { data } = await axios.get(DADOS_URL, { params: this.queryParams() });
        this.dados = data;
        await this.$nextTick();
        this.renderCharts();
        await this.carregarLista();
      } catch (e) {
        this.erro = "Não foi possível carregar o dashboard.";
        this.dados = null;
        this.listaData = [];
      } finally {
        this.loading = false;
      }
    },
    async limparFiltros() {
      this.filters = {
        data_ini: firstDayOfMonthYmd(),
        data_fim: todayYmd(),
        colunas: [],
        donos: [],
        grupos: [],
      };
      await this.aplicarFiltros();
    },
    async carregarLista() {
      try {
        const { data } = await axios.get(CLIENTES_URL, { params: this.queryParams() });
        if (data && data.url) {
          const res = await axios.get(data.url, { withCredentials: false });
          this.listaData = res.data.data || [];
          return;
        }
        this.listaData = data.data || [];
      } catch (e) {
        this.erro = "Não foi possível carregar a lista de clientes.";
        this.listaData = [];
      }
    },
    destroyCharts() {
      if (this.chartReunioesPorDonoInstance) {
        this.chartReunioesPorDonoInstance.destroy();
        this.chartReunioesPorDonoInstance = null;
      }
      if (this.chartVisitasPorDonoInstance) {
        this.chartVisitasPorDonoInstance.destroy();
        this.chartVisitasPorDonoInstance = null;
      }
      if (this.chartSemaforoInstance) {
        this.chartSemaforoInstance.destroy();
        this.chartSemaforoInstance = null;
      }
    },
    seriePorNome(series, nome) {
      if (!Array.isArray(series)) {
        return [];
      }
      const encontrada = series.find((item) => item && item.name === nome);
      return encontrada && Array.isArray(encontrada.data) ? encontrada.data : [];
    },
    renderBarPorDono(refName, instanceKey, nomeSerie, cor, tipo, campoValor) {
      const el = this.$refs[refName];
      if (!el) {
        return;
      }
      const listaPorDono = this.dados.por_dono || [];
      el.style.cursor = "pointer";
      this[instanceKey] = new window.ApexCharts(el, {
        chart: {
          type: "bar",
          height: 280,
          toolbar: { show: false },
          fontFamily: "inherit",
          events: {
            dataPointSelection: (_event, _chartContext, config) => {
              const index = Number(config.dataPointIndex);
              if (Number.isNaN(index) || index < 0) {
                return;
              }
              const dono = listaPorDono[index];
              if (!dono) {
                return;
              }
              this.abrirListaTarefas(dono, tipo);
            },
          },
        },
        series: [{
          name: nomeSerie,
          data: listaPorDono.map((item) => Number(item[campoValor]) || 0),
        }],
        xaxis: { categories: listaPorDono.map((item) => item.nome || "") },
        plotOptions: { bar: { borderRadius: 6, columnWidth: "50%" } },
        dataLabels: { enabled: true },
        colors: [cor],
        grid: { borderColor: "rgba(0,0,0,0.06)", strokeDashArray: 4 },
        noData: { text: "Sem dados no período" },
      });
      this[instanceKey].render();
    },
    showModalTarefas() {
      const jq = typeof window !== "undefined" ? window.jQuery || window.$ : null;
      if (jq) {
        jq("#modalCarteiraDashboardTarefas").modal("show");
      }
    },
    async abrirListaTarefas(dono, tipo) {
      const tipoLabel = tipo === "visita" ? "Visitas" : "Reuniões";
      const donoNome = dono.nome || "Sem dono";
      const requestId = ++this.modalRequestId;
      this.modalTitulo = `${tipoLabel} — ${donoNome}`;
      this.modalErro = "";
      this.modalTarefas = [];
      this.modalLoading = true;

      this.$nextTick(() => {
        this.showModalTarefas();
      });

      try {
        const params = {
          ...this.queryParams(),
          tipo,
        };
        if (dono.dono_id == null) {
          params.sem_dono = 1;
        } else {
          params.dono_id = dono.dono_id;
        }
        const response = await axios.get(TAREFAS_URL, { params });
        if (requestId !== this.modalRequestId) {
          return;
        }
        const payload = response && response.data ? response.data : {};
        this.modalTarefas = Array.isArray(payload.data) ? payload.data : [];
        if (payload.dono_nome) {
          this.modalTitulo = `${tipoLabel} — ${payload.dono_nome}`;
        }
      } catch (e) {
        if (requestId !== this.modalRequestId) {
          return;
        }
        this.modalErro = "Não foi possível carregar as tarefas.";
        this.modalTarefas = [];
      } finally {
        if (requestId === this.modalRequestId) {
          this.modalLoading = false;
        }
      }
    },
    renderCharts() {
      this.destroyCharts();
      if (typeof window.ApexCharts === "undefined" || !this.dados) {
        return;
      }

      this.renderBarPorDono(
        "chartReunioesPorDono",
        "chartReunioesPorDonoInstance",
        "Reuniões",
        "#4e73df",
        "reuniao",
        "reunioes_periodo"
      );
      this.renderBarPorDono(
        "chartVisitasPorDono",
        "chartVisitasPorDonoInstance",
        "Visitas",
        "#f6c23e",
        "visita",
        "visitas_periodo"
      );

      const semaforo = this.dados.grafico_semaforo || { labels: [], series: [] };
      if (this.$refs.chartSemaforo) {
        this.chartSemaforoInstance = new window.ApexCharts(this.$refs.chartSemaforo, {
          chart: { type: "donut", height: 280, fontFamily: "inherit" },
          series: semaforo.series || [],
          labels: semaforo.labels || [],
          colors: ["#1cc88a", "#f6c23e", "#e74a3b", "#858796"],
          legend: { position: "bottom" },
          plotOptions: {
            pie: {
              donut: {
                size: "62%",
              },
            },
          },
          noData: { text: "Sem dados" },
        });
        this.chartSemaforoInstance.render();
      }
    },
    iniciais(nome) {
      const parts = String(nome || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) {
        return "?";
      }
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
    coberturaPercent(item) {
      const total = Number(item.total_clientes) || 0;
      if (total <= 0) {
        return 0;
      }
      const atendidos = Number(item.atendidos_periodo) || 0;
      return Math.min(100, Math.round((atendidos / total) * 100));
    },
    indicadorClass(indicador) {
      if (indicador === "verde") return "text-success font-weight-bold";
      if (indicador === "amarelo") return "text-warning font-weight-bold";
      if (indicador === "vermelho") return "text-danger font-weight-bold";
      return "text-muted";
    },
    indicadorTitle(indicador) {
      if (indicador === "verde") return "Verde: última reunião/visita há até 1 mês";
      if (indicador === "amarelo") return "Amarelo: última reunião/visita entre 1 e 3 meses";
      if (indicador === "vermelho") return "Vermelho: última reunião/visita há mais de 3 meses";
      return "Sem reunião/visita concluída";
    },
  },
};
</script>
